export const stripApiMessageFields = (plan) => {
  if (!plan || typeof plan !== "object") return plan;

  const { showMessage, returnDesc, ...rest } = plan;
  return rest;
};

const normalizePayload = (source) => {
  if (!source) return {};

  if (source?.data !== undefined) {
    const { data } = source;
    if (Array.isArray(data)) return data[0] ?? {};
    return data ?? {};
  }

  return source;
};

export const shouldShowApiMessage = (payload) => {
  if (!payload || typeof payload !== "object") return false;
  return String(payload.showMessage) === "1";
};

export const resolveApiSuccessMessage = (defaultMessage, ...sources) => {
  for (const source of sources) {
    const payload = normalizePayload(source);

    if (shouldShowApiMessage(payload)) {
      return payload.returnDesc || defaultMessage;
    }
  }

  return defaultMessage;
};

export const getApiSuccessMessage = (response, defaultMessage) =>
  resolveApiSuccessMessage(defaultMessage, response);

export const resolveApiMessageSeverity = (...sources) => {
  for (const source of sources) {
    const payload = normalizePayload(source);
    if (shouldShowApiMessage(payload)) {
      return "warning";
    }
  }

  return "success";
};

const normalizeMilestoneName = (name) => {
  if (name === "DURATION_TO_SITE_ID_FINISH") return "DURATION_TO_SITE_ID";
  return name;
};

const dayKeys = (millis) => {
  const value = Number(millis);
  if (!Number.isFinite(value)) return [];
  const utc = new Date(value);
  const local = new Date(value);
  return [
    `u:${utc.getUTCFullYear()}-${utc.getUTCMonth()}-${utc.getUTCDate()}`,
    `l:${local.getFullYear()}-${local.getMonth()}-${local.getDate()}`,
  ];
};

const sameCalendarDay = (a, b) => {
  const keysA = new Set(dayKeys(a));
  if (!keysA.size) return false;
  return dayKeys(b).some((key) => keysA.has(key));
};

const isNearCroStart = (millis, croMillis) => {
  if (!Number.isFinite(Number(millis)) || !Number.isFinite(Number(croMillis))) {
    return false;
  }
  if (sameCalendarDay(millis, croMillis)) return true;
  // Backend/client timezone drift can be up to ~1 day.
  return Math.abs(Number(millis) - Number(croMillis)) < 36 * 60 * 60 * 1000;
};

const collectMilestoneDates = (plan) => {
  const dates = new Map();

  for (const country of plan?.countries || []) {
    const countryId = String(country.countryId ?? "");
    for (const siteGroup of country.siteGroups || []) {
      for (const milestone of siteGroup.milestones || []) {
        if (milestone?.milestoneDateMillis == null || !milestone.milestoneName) {
          continue;
        }
        const key = `${countryId}|${normalizeMilestoneName(milestone.milestoneName)}`;
        dates.set(key, Number(milestone.milestoneDateMillis));
      }
    }
  }

  return dates;
};

/** Countries whose milestone dates changed on this save. */
export const getCountriesWithMilestoneDateChanges = (beforePlan, afterPlan) => {
  const beforeDates = collectMilestoneDates(beforePlan);
  const afterDates = collectMilestoneDates(afterPlan);
  const changed = new Set();

  for (const [key, afterMillis] of afterDates) {
    const beforeMillis = beforeDates.get(key);
    if (beforeMillis == null) continue;
    if (!sameCalendarDay(beforeMillis, afterMillis)) {
      changed.add(key.split("|")[0]);
    }
  }

  return [...changed];
};

/**
 * True when the server moved at least one milestone date onto CRO Start during this save.
 */
export const milestonesClampedToCroStart = (beforePlan, afterPlan) => {
  const croMillis =
    afterPlan?.croProjectStartDateMillis ?? beforePlan?.croProjectStartDateMillis;
  if (!Number.isFinite(Number(croMillis))) return false;

  const beforeDates = collectMilestoneDates(beforePlan);
  const afterDates = collectMilestoneDates(afterPlan);

  for (const [key, afterMillis] of afterDates) {
    if (!isNearCroStart(afterMillis, croMillis)) continue;

    const beforeMillis = beforeDates.get(key);
    if (beforeMillis == null) continue;

    if (!sameCalendarDay(beforeMillis, afterMillis)) {
      return true;
    }
  }

  return false;
};

const getCountryDirectory = (...plans) => {
  const byName = new Map();

  for (const plan of plans) {
    for (const country of plan?.countries || []) {
      if (!country?.countryName || country.countryId == null) continue;
      byName.set(String(country.countryName).toLowerCase(), String(country.countryId));
    }
  }

  return byName;
};

/** Country IDs mentioned in an API CRO warning message. */
export const extractMentionedCountryIds = (message, ...plans) => {
  if (!message) return [];
  const directory = getCountryDirectory(...plans);
  const mentioned = [];

  for (const [name, id] of directory) {
    if (String(message).toLowerCase().includes(name)) {
      mentioned.push(id);
    }
  }

  return mentioned;
};

const getReturnDesc = (...sources) => {
  for (const source of sources) {
    const payload = normalizePayload(source);
    if (payload.returnDesc) return payload.returnDesc;
  }
  return null;
};

/**
 * Save snackbar with per-country independence:
 * - warning when this save newly clamps to CRO, or
 * - warning when the API warning mentions a country whose milestones changed, or
 * - success when the API warning is sticky from another country
 */
export const resolveSavePlanSnackbar = (
  defaultMessage,
  beforePlan,
  afterPlan,
  ...sources
) => {
  const hasApiWarning = resolveApiMessageSeverity(...sources) === "warning";
  if (!hasApiWarning) {
    return { message: defaultMessage, severity: "success" };
  }

  const apiMessage =
    resolveApiSuccessMessage(defaultMessage, ...sources) ||
    getReturnDesc(...sources) ||
    defaultMessage;

  if (milestonesClampedToCroStart(beforePlan, afterPlan)) {
    return { message: apiMessage, severity: "warning" };
  }

  const changedCountryIds = getCountriesWithMilestoneDateChanges(
    beforePlan,
    afterPlan,
  );
  const mentionedCountryIds = extractMentionedCountryIds(
    apiMessage,
    beforePlan,
    afterPlan,
  );

  if (mentionedCountryIds.length === 0) {
    // Message has no country name we can match — keep warning if anything changed.
    if (changedCountryIds.length > 0) {
      return { message: apiMessage, severity: "warning" };
    }
    return { message: defaultMessage, severity: "success" };
  }

  const changed = new Set(changedCountryIds.map(String));
  const overlaps = mentionedCountryIds.some((id) => changed.has(String(id)));
  if (overlaps) {
    return { message: apiMessage, severity: "warning" };
  }

  // Sticky warning about another country — independent success.
  return { message: defaultMessage, severity: "success" };
};